import { store } from '@store';
import { DateHelper } from '@utils/DateHelper';
import uuid from 'uuid';
import castArray from 'lodash/castArray';
import { makeAutoObservable, runInAction } from 'mobx';
import { Certificate, createDetachedSignature, createHash, getUserCertificates, isValidSystemSetup } from 'crypto-pro';

import type { Optional } from 'chaika-frontend-types/frontend-cms';
import type { OptionType } from '@components/Select';

export class AuthUserCertificates {
    public static Name = 'authUserCertificates' as const;

    public static toOptionType(certificate: Certificate | Certificate[]): OptionType<string>[] {
        return castArray(certificate).map(it => ({
            label: it.name,
            value: it.thumbprint,
        }));
    }

    public selectedCertificate?: Certificate;
    public hasCryptoPro = false;
    private certificates: Certificate[] = [];
    private readonly localDev = process.env.CMS_ENV === 'local-dev';

    constructor() {
        makeAutoObservable(this, undefined, { autoBind: true });
    }

    public get containerCertificates(): Certificate[] {
        return this.certificates;
    }

    public get certificateOptions(): OptionType<string>[] {
        return this.containerCertificates.flatMap(AuthUserCertificates.toOptionType);
    }

    public async signContent(thumbprint: string, content: string | ArrayBuffer): Promise<Optional<string>> {
        try {
            if (this.localDev) {
                return uuid();
            } else {
                const hash = await createHash(content);
                return await createDetachedSignature(thumbprint, hash);
            }
        } catch (e: unknown) {
            console.warn(e);
            return undefined;
        }
    }

    public async updateCryptoProInfo(): Promise<void> {
        try {
            const hasCryptoPro = this.localDev || (await isValidSystemSetup());

            runInAction(() => {
                this.hasCryptoPro = hasCryptoPro;
            });
        } catch (e: unknown) {
            console.warn(e);
        }
    }

    public async setContainerCertificates(): Promise<void> {
        if (this.hasCryptoPro) {
            try {
                const certificates: Certificate[] = this.localDev
                    ? [
                          {
                              name: store.authUser.fullName,
                              thumbprint: 'thumbprint',
                              validFrom: DateHelper.subDays(new Date(), 10).toISOString(),
                              validTo: DateHelper.addDays(new Date(), 10).toISOString(),
                              ownerName: store.authUser.firstName,
                          } as unknown as Certificate,
                      ]
                    : await getUserCertificates(true);

                runInAction(() => {
                    this.certificates = certificates;
                });
            } catch (e: unknown) {
                console.warn(e);
            }
        }
    }

    public setSelectedCertificate(value?: Certificate): void {
        this.selectedCertificate = value;
    }
}

export const authUserCertificates = new AuthUserCertificates();
